import { useCallback, useId, useState, type ComponentType, type SVGProps } from "react";
import type { MeetingPrepCardHide, MeetingPrepCardReveal, MeetingPrepLearnedDefault } from "@acto/taskpane/core/domain";
import { styled } from "@pigment-css/react";
import type { Sentiment, StoryCard as StoryCardSchema, TalkingPoint } from "@acto/genui-components-schema";
import { useTranslations } from "@acto/web-common/locales";
import { BarChartAxis, Checklist, ShoppingBagBlock, Sprout, TrashCanSimple } from "@acto/web-common/components";
import { GlassButton } from "@acto/taskpane/field-sales-2/components/primitives/GlassButton";
import { Collapse } from "@acto/taskpane/field-sales-2/components/primitives/Collapse";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { MoreButton } from "@acto/taskpane/field-sales-2/components/primitives/MoreButton";
import { MenuPopover } from "@acto/taskpane/field-sales-2/components/primitives/MenuPopover";
import { isProductHideable } from "@acto/taskpane/field-sales-2/utils/productHide";
import { Trend } from "./Trend";
import { OrderPulseHero } from "./OrderPulseHero";
import { OrderPulseCalendar } from "./OrderPulseCalendar";
import { GapList } from "./GapList";
import { QuestionPrompts } from "./QuestionPrompts";
import { Offer } from "./Offer";
import { KpiDelta } from "./KpiDelta";
import { Overview } from "./Overview";
import { EntityList } from "./EntityList";
import { OrderCard } from "./OrderCard";
import { ReentryCard } from "./ReentryCard";
import { BasketCard } from "./BasketCard";
import { LifetimeCard } from "./LifetimeCard";
import { HidableTalkingPoint } from "./HidableTalkingPoint";
import { TodoChecklist } from "./todo-checklist/TodoChecklist";
import {
  isTodoChecklistRenderable,
  isTodoStory,
  useMeetingPrepTodosContext,
} from "./todo-checklist/MeetingPrepTodosContext";

const toneForCard = (card: StoryCardSchema): Sentiment => card.sentiment ?? "neutral";

const Frame = styled("div")<{ expanded: boolean }>(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  paddingTop: theme.fieldSalesTwo.spacing[24],
  paddingBottom: theme.fieldSalesTwo.spacing[24],
  borderRadius: theme.fieldSalesTwo.radius["44"],
  background: theme.fieldSalesTwo.colors.surface.glass,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorder}`,
  boxSizing: "border-box",
  transition: `padding-bottom ${theme.fieldSalesTwo.motion.duration.slow} ${theme.fieldSalesTwo.motion.easing.emphasized}`,
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
  // Expanded, the last talking-point card supplies its own bottom padding, so the frame tightens.
  variants: [{ props: { expanded: true }, style: { paddingBottom: theme.fieldSalesTwo.spacing[8] } }],
}));

const TopStack = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[24],
  paddingLeft: theme.fieldSalesTwo.spacing[24],
  paddingRight: theme.fieldSalesTwo.spacing[24],
}));

const Header = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[12],
}));

const CategoryRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
}));

const CategoryIconBox = styled("span")<{ tone: Sentiment }>(({ theme }) => ({
  display: "inline-flex",
  flexShrink: 0,
  color: theme.fieldSalesTwo.colors.charts.primary,
  variants: [
    {
      props: { tone: "positive" },
      style: { color: theme.fieldSalesTwo.colors.status.success.main },
    },
    {
      props: { tone: "negative" },
      style: { color: theme.fieldSalesTwo.colors.status.error.main },
    },
  ],
}));

const iconForTone: Record<Sentiment, ComponentType<SVGProps<SVGSVGElement>>> = {
  positive: Sprout,
  negative: ShoppingBagBlock,
  neutral: BarChartAxis,
};

const CategoryLabel = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["15"],
  fontWeight: theme.fieldSalesTwo.font.weight["520"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["148"],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const StoryHeadline = styled("h2")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["21"],
  fontWeight: theme.fieldSalesTwo.font.weight["600"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["120"],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const Description = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight["440"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["164"],
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const Potential = styled("div")<{ tone: Sentiment }>(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.fieldSalesTwo.spacing[10],
  padding: `${theme.fieldSalesTwo.spacing[14]} ${theme.fieldSalesTwo.spacing[20]}`,
  borderRadius: theme.fieldSalesTwo.radius.circular,
  background: theme.fieldSalesTwo.colors.surface.glass,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  variants: [
    {
      props: { tone: "positive" },
      style: {
        background: theme.fieldSalesTwo.colors.status.success.tint,
        borderColor: theme.fieldSalesTwo.colors.status.success.tint,
      },
    },
    {
      props: { tone: "negative" },
      style: {
        background: theme.fieldSalesTwo.colors.status.error.tint,
        borderColor: theme.fieldSalesTwo.colors.status.error.tint,
      },
    },
  ],
}));

const PotentialLabel = styled("span")<{ tone: Sentiment }>(({ theme }) => ({
  color: theme.fieldSalesTwo.colors.neutral[800],
  variants: [
    {
      props: { tone: "positive" },
      style: { color: theme.fieldSalesTwo.colors.status.success.main },
    },
    {
      props: { tone: "negative" },
      style: { color: theme.fieldSalesTwo.colors.status.error.main },
    },
  ],
}));

const PotentialValue = styled("span")<{ tone: Sentiment }>(({ theme }) => ({
  color: theme.fieldSalesTwo.colors.neutral[900],
  variants: [
    {
      props: { tone: "positive" },
      style: { color: theme.fieldSalesTwo.colors.status.success.main },
    },
    {
      props: { tone: "negative" },
      style: { color: theme.fieldSalesTwo.colors.status.error.main },
    },
  ],
}));

// Full-width secondary variant of the shared glass capsule.
const ToggleButton = styled(GlassButton)(({ theme }) => ({
  width: "100%",
  fontWeight: theme.fieldSalesTwo.font.weight["440"],
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const FooterRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
}));

const TalkingPoints = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  paddingTop: theme.fieldSalesTwo.spacing[24],
  paddingLeft: theme.fieldSalesTwo.spacing[8],
  paddingRight: theme.fieldSalesTwo.spacing[8],
}));

interface StoryCardProps {
  card: StoryCardSchema;
  onRemoveRequest?: () => void;
  /** Needed for per-card hides; cards render plain without it. */
  clientId?: string;
  /** Session id of the generated prep; per-card hides are scoped to it. */
  prepSessionId?: string | null;
  /** The viewer's ACTIVE card hides for the client. Undefined disables per-card hides. */
  cardHides?: MeetingPrepCardHide[];
  /** The viewer's card reveals in this prep. A revealed card ignores its learned default. */
  cardReveals?: MeetingPrepCardReveal[];
  learnedDefaults?: MeetingPrepLearnedDefault[];
}

export function StoryCard({
  card,
  onRemoveRequest,
  clientId,
  prepSessionId,
  cardHides,
  cardReveals,
  learnedDefaults,
}: StoryCardProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.storyCard");
  const tRemoval = useTranslations("fieldSalesTwo.genuiMeetingPrep.storyCard.removal");
  const tone = toneForCard(card);
  const todosState = useMeetingPrepTodosContext();
  // An empty checklist must not leave a bare shell behind (auto-dismissed content)
  const visiblePoints = card.talkingPoints.filter(
    (point) => point.type !== "todoChecklist" || isTodoChecklistRenderable(point, todosState),
  );
  const activeHideFor = (point: TalkingPoint) =>
    point.cardKey ? cardHides?.find((hide) => hide.cardKey === point.cardKey) : undefined;
  const isRevealed = (cardKey: string) => cardReveals?.some((reveal) => reveal.cardKey === cardKey) ?? false;
  const learnedDefaultFor = (point: TalkingPoint) =>
    point.cardKey && !isRevealed(point.cardKey)
      ? learnedDefaults?.find((learnedDefault) => learnedDefault.cardKey === point.cardKey)
      : undefined;
  // A card showing its undo notice keeps its slot; it moves to the end only once it has collapsed.
  const [settlingIds, setSettlingIds] = useState<ReadonlySet<string>>(new Set());
  const markSettling = useCallback((id: string, settling: boolean) => {
    setSettlingIds((current) => {
      if (current.has(id) === settling) return current;
      const next = new Set(current);
      if (settling) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);
  // Slot ids are stable across reordering and across a checklist dropping out of visiblePoints:
  // several cards can share a cardKey (offers), so the payload position disambiguates them, and
  // the prep's session id keeps per-slot state from surviving into a newly generated prep.
  const slots = visiblePoints.map((point) => ({
    point,
    id: `${prepSessionId ?? ""}:${point.cardKey ?? point.type}:${card.talkingPoints.indexOf(point)}`,
  }));
  const isHidden = ({ point, id }: (typeof slots)[number]) =>
    !settlingIds.has(id) && (activeHideFor(point) !== undefined || learnedDefaultFor(point) !== undefined);
  // Hidden cards collapse to a row at the end of the story, in payload order.
  const orderedSlots = [...slots.filter((slot) => !isHidden(slot)), ...slots.filter(isHidden)];
  const ToneIcon = isTodoStory(card) ? Checklist : iconForTone[tone];
  const [expanded, setExpanded] = useState(false);
  const id = useId();
  const headlineId = `${id}-headline`;
  const pointsId = `${id}-points`;

  return (
    <Frame expanded={expanded}>
      <TopStack>
        <Header>
          <CategoryRow>
            <CategoryIconBox tone={tone}>
              <ToneIcon width={20} height={20} aria-hidden />
            </CategoryIconBox>
            <CategoryLabel>{card.category}</CategoryLabel>
          </CategoryRow>
          <StoryHeadline id={headlineId}>{card.storyHeadline}</StoryHeadline>
          <Description>{card.description}</Description>
          {card.potential && (
            <Potential tone={tone}>
              <PotentialLabel tone={tone}>{t("potentialLabel")}</PotentialLabel>
              <PotentialValue tone={tone}>{card.potential}</PotentialValue>
            </Potential>
          )}
        </Header>
        <FooterRow>
          {onRemoveRequest && (
            <MenuPopover
              trigger={<MoreButton aria-label={tRemoval("moreOptions")} />}
              align="start"
              surface="capsule"
              ariaLabel={tRemoval("moreOptions")}
              items={[
                {
                  label: tRemoval("remove"),
                  icon: <TrashCanSimple aria-hidden="true" />,
                  tone: "destructive",
                  onSelect: onRemoveRequest,
                },
              ]}
            />
          )}
          <ToggleButton
            type="button"
            aria-expanded={expanded}
            aria-controls={pointsId}
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? t("collapse") : t("expand")}
          </ToggleButton>
        </FooterRow>
      </TopStack>
      <Collapse open={expanded} id={pointsId} role="group" aria-labelledby={headlineId}>
        {visiblePoints.length > 0 && (
          <TalkingPoints>
            <Divider />
            {orderedSlots.map(({ point, id }) => {
              const cardKey = point.cardKey;
              const cardHide =
                cardHides !== undefined && prepSessionId && cardKey ? { prepSessionId, cardKey } : undefined;
              const learnedDefault = learnedDefaultFor(point);
              if (!clientId || (!cardHide && !learnedDefault && !isProductHideable(point))) {
                return <TalkingPointRenderer key={id} point={point} tone={tone} />;
              }
              return (
                <HidableTalkingPoint
                  key={id}
                  point={point}
                  clientId={clientId}
                  cardHide={cardHide}
                  activeHide={activeHideFor(point)}
                  learnedDefault={learnedDefault}
                  onSettling={(settling) => markSettling(id, settling)}
                  renderCard={() => <TalkingPointRenderer point={point} tone={tone} />}
                />
              );
            })}
          </TalkingPoints>
        )}
      </Collapse>
    </Frame>
  );
}

function TalkingPointRenderer({ point, tone }: { point: TalkingPoint; tone: Sentiment }) {
  switch (point.type) {
    case "trend":
      return <Trend part={point} />;
    case "orderPulseHero":
      return <OrderPulseHero part={point} />;
    case "orderPulseCalendar":
      return <OrderPulseCalendar part={point} />;
    case "gapList":
      return <GapList part={point} />;
    case "questionPrompts":
      return <QuestionPrompts part={point} sentiment={tone} />;
    case "offer":
      return <Offer part={point} />;
    case "kpiDelta":
      return <KpiDelta part={point} />;
    case "overview":
      return <Overview part={point} />;
    case "entityList":
      return <EntityList part={point} />;
    case "todoChecklist":
      return <TodoChecklist part={point} />;
    case "orderCard":
      return <OrderCard part={point} />;
    case "reentryCard":
      return <ReentryCard part={point} />;
    case "basketCard":
      return <BasketCard part={point} />;
    case "lifetimeCard":
      return <LifetimeCard part={point} />;
    default: {
      point satisfies never;
      return null;
    }
  }
}
